import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CK_Schemas from '../../DataServices/CK_SchemasDS';
import { useCustomContext } from '../CustomComponents/CustomComponents';
import { insertLog } from "../Logs/InsertLog";
export const useCKSchemaLogic = () => {

    const useremail = localStorage.getItem("email");
    const userName = useremail?.split("@")[0];
    const navigate = useNavigate();
    const ck_schemasDS = new CK_Schemas();
    const dsApi = ck_schemasDS;

    //  ALL STATE HOOKS
    const [ckschemas, setCKschemas] = useState([]);

    // PAGINATION & SEARCH STATE (COMMON PATTERN)
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [search, setSearch] = useState('');

    const [openJSONModal, setOpenJSONModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const [jsonSchema, setJsonSchema] = useState({});
    const [copyObject, setCopyObject] = useState({});
    const [jsonText, setJsonText] = useState("");
    const [isJSONSaved, setIsJSONSaved] = useState(false);
    const [schemaFormData, setSchemaFormData] = useState({});
    const [schemaErrors, setSchemaErrors] = useState({});
    const [editingSchemaId, setEditingSchemaId] = useState(null);
    const [currentSchemaName, setCurrentSchemaName] = useState("");

    const { hud, stopHudRotation, showAlert, showToast } = useCustomContext();

    //  FILTER + SORT (SEARCH STEP) BY LABEL
    const filteredSchemas = ckschemas
        .filter(s => s.label?.toLowerCase().includes(search.toLowerCase()));

    //PAGINATION CALCULATIONS
    const totalRecords = filteredSchemas.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const displayedSchemas = filteredSchemas.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const startEntry = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endEntry = Math.min(currentPage * pageSize, totalRecords);

    // FETCH ALL SCHEMAS ON MOUNT TO LIST
    useEffect(() => {
        fetchCKSchemas();
    }, []);

    const fetchCKSchemas = async () => {
        hud("Please wait...");
        try {
            const response = await ck_schemasDS.WS_CK_Schemas_List_GET({});
            const data = response.data;
            const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

            if (data.statusCode === 200 && Array.isArray(body.data)) {
                setCKschemas(body.data);
            } else {
                insertLog(
                    `${userName} failed to load CK Schemas`,
                    0,
                    useremail,
                    hud,
                    stopHudRotation,
                    showAlert,
                    '',
                    'GET',
                    'WS_CK_Schemas_List_GET',
                    'FAILED',
                    'Unable to load CK Schemas'
                );
                showAlert(body?.message || "Failed to load CK Schemas.", [
                    { label: "OK", color: "var(--buttonColor)", onClick: () => { } }
                ]);
            }
        } catch (err) {
            insertLog(
                `${userName} failed to fetch CK Schemas`,
                0,
                useremail,
                hud,
                stopHudRotation,
                showAlert,
                '',
                'GET',
                'WS_CK_Schemas_List_GET',
                'FAILED',
                err.message
            );
            showAlert(err.message || "Something went wrong while fetching CK Schemas.", [
                { label: "OK", color: "var(--buttonColor)", onClick: () => { } }
            ]);
        } finally {
            stopHudRotation();
        }
    };

    // VIEW JSON
    const viewJSON = async (id) => {
        if (!id) return;
        hud("Please wait...");
        try {
            const response = await ck_schemasDS.WS_CK_Schema_Object_By_ID_GET({ _id: id });
            const data = response.data;
            const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

            if (data.statusCode === 200 && body?.data) {
                let parsedData = typeof body.data === "string"
                    ? JSON.parse(body.data)
                    : body.data;

                const { _id, created_by_email_id, updated_by_email_id, schema_name, ...dataWithoutId } = parsedData;
                setCurrentSchemaName(schema_name || "Unknown Schema");
                setEditingSchemaId(_id);
                setJsonSchema(dataWithoutId);
                setCopyObject(JSON.parse(JSON.stringify(dataWithoutId)));
                setSchemaFormData(dataWithoutId);
                setJsonText(JSON.stringify(dataWithoutId, null, 2));
                setIsEditMode(false);
                setSchemaErrors({});
                setOpenJSONModal(true);
            } else {
                insertLog(
                    `${userName} failed to fetch schema JSON for id ${id}`,
                    0,
                    useremail,
                    hud,
                    stopHudRotation,
                    showAlert,
                    id || '',
                    'GET',
                    'WS_CK_Schema_Object_By_ID_GET',
                    'FAILED',
                    'Unable to fetch schema JSON'
                );
                showAlert(body?.message || "Failed to fetch schema JSON.", [
                    { label: "OK", color: "var(--buttonColor)", onClick: () => { } }
                ]);
            }
        } catch (err) {
            insertLog(
                `${userName} failed to fetch schema JSON for id ${id}`,
                0,
                useremail,
                hud,
                stopHudRotation,
                showAlert,
                id || '',
                'GET',
                'WS_CK_Schema_Object_By_ID_GET',
                'FAILED',
                err.message
            );
            showAlert(err.message || "Something went wrong while fetching schema JSON.", [
                { label: "OK", color: "var(--buttonColor)", onClick: () => { } }
            ]);
        } finally {
            stopHudRotation();
        }
    };

    //JSON EDIT HANDLERS
    const handleEnableEdit = () => {
        setIsEditMode(true);
    };

    const handleJsonChange = (e) => {
        const value = e.target.value;
        setJsonText(value);
        try {
            const parsed = JSON.parse(value);

            const { _id, created_by_email_id, updated_by_email_id, ...cleaned } = parsed;

            setJsonSchema(cleaned);
            setSchemaFormData(cleaned);
            setSchemaErrors({});
        } catch {
            setSchemaErrors({ json: "Invalid JSON format" });
        }
    };

    const handleCancelEdit = () => {
        const isSame = deepEqual(copyObject, jsonSchema);

        if (!isSame) {
            showAlert(
                "You have unsaved changes. Do you want to cancel?",
                [
                    {
                        label: "Yes",
                        color: "var(--buttonColor)",
                        onClick: () => {
                            setJsonSchema(copyObject);
                            setJsonText(JSON.stringify(copyObject, null, 2));
                            setIsEditMode(false);
                            setSchemaErrors({});
                        }
                    },
                    {
                        label: "No",
                        color: "#999",
                        onClick: () => { }
                    }

                ]
            );
        } else {
            setJsonSchema(copyObject);
            setJsonText(JSON.stringify(copyObject, null, 2));
            setIsEditMode(false);
            setSchemaErrors({});
        }
    };

    const handleDownloadJSON = () => {
        const blob = new Blob(
            [JSON.stringify(jsonSchema, null, 2)],
            { type: "application/json" }
        );

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const filename = schemaFormData?.schema_name || currentSchemaName || "schema";
        a.download = `${filename}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    //Alert when json not saved
    const handleCloseModal = () => {
        const isSame = deepEqual(copyObject, jsonSchema);

        if (!isSame && isEditMode) {
            showAlert("You have unsaved changes. Do you want to cancel?", [
                {
                    label: "Yes",
                    color: "var(--buttonColor)",
                    onClick: () => setOpenJSONModal(false)
                },
                {
                    label: "No",
                    color: "#999",
                    onClick: () => { }
                }
            ]);
        } else {
            setOpenJSONModal(false);
        }
    };

    // UPDATE TO SERVER
    const handleUpdateToServer = async () => {
        const isSame = deepEqual(copyObject, jsonSchema);

        if (isSame) {        
            showAlert("No modifications have been identified.", [
                { label: "Ok", color: "var(--buttonColor)", onClick: () => { } }
            ]);
            return;
        }

        const errors = validateSchemaForm(schemaFormData);
        setSchemaErrors(errors);
        if (Object.keys(errors).length > 0) return;

        try {
            hud("Please wait...");

            const hasDatasetPurposeChanged =
                normalizeValue(copyObject.dataset_id).toLowerCase() !== normalizeValue(jsonSchema.dataset_id).toLowerCase() ||
                normalizeValue(copyObject.purpose).toLowerCase() !== normalizeValue(jsonSchema.purpose).toLowerCase();

            if (hasDatasetPurposeChanged) {
                const duplicateDatasetPurpose = await checkDuplicateDatasetPurpose(
                    jsonSchema.dataset_id,
                    jsonSchema.purpose,
                    editingSchemaId
                );

                if (duplicateDatasetPurpose) {
                    stopHudRotation();
                    showAlert(
                        `${jsonSchema.dataset_id} cannot update a schema with this existing ${jsonSchema.purpose} purpose`,
                        [{ label: "Ok", color: "var(--buttonColor)", onClick: () => { } }]
                    );
                    return;
                }
            }

            const resp = await dsApi.WS_CK_Schema_ByID_UPDATE({
                _id: editingSchemaId,
                ...jsonSchema,
                updated_by_email_id: useremail 
            });

            const data = resp?.data;
            const body =
                typeof data?.body === "string"
                    ? JSON.parse(data.body)
                    : data?.body;

            if (data?.statusCode !== 200) {
                insertLog(
                    `${userName} failed to update schema ${jsonSchema?.schema_name}`,
                    0,
                    useremail,
                    hud,
                    stopHudRotation,
                    showAlert,
                    editingSchemaId || '',
                    'UPDATE',
                    'WS_CK_Schema_ByID_UPDATE',
                    'FAILED',
                    'Unable to update CK Schema'
                );
                stopHudRotation();
                showAlert(body?.message || "Failed to update CK Schema", [
                    { label: "Ok", color: "var(--buttonColor)", onClick: () => { } }
                ]);
                return;
            }

            await fetchCKSchemas();
            insertLog(
                `${userName} updated schema ${jsonSchema?.schema_name}`,
                0,
                useremail,
                hud,
                stopHudRotation,
                showAlert,
                editingSchemaId || '',
                'UPDATE',
                'WS_CK_Schema_ByID_UPDATE',
                'SUCCESS',
                ''
            );

            stopHudRotation();
            setOpenJSONModal(false);
            setIsJSONSaved(true);

            showToast({ message: "CK Schema updated successfully" });
        } catch (err) {
            insertLog(
                `${userName} failed to update schema ${jsonSchema?.schema_name}`,
                0,
                useremail,
                hud,
                stopHudRotation,
                showAlert,
                editingSchemaId || '',
                'UPDATE',
                'WS_CK_Schema_ByID_UPDATE',
                'FAILED',
                err.message
            );

            stopHudRotation();
            console.error("Update failed:", err);
            showAlert(err.message || "Error updating CK Schema!", [
                { label: "Ok", color: "var(--buttonColor)", onClick: () => { } }
            ]);
        }
    };

    const deepEqual = (a, b) => {
        try {
            return JSON.stringify(a) === JSON.stringify(b);
        } catch {
            return false;
        }
    };

    const normalizeValue = (value) => typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";

    const checkDuplicateDatasetPurpose = async (datasetId, purpose, excludeSchemaId = null) => {
        const normalizedDatasetId = normalizeValue(datasetId).toLowerCase();
        const normalizedPurpose = normalizeValue(purpose).toLowerCase();

        if (!normalizedDatasetId || !normalizedPurpose) return false;

        try {
            const response = await dsApi.WS_CK_Schemas_List_GET({});
            const data = response.data;
            const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

            if (data.statusCode === 200 && Array.isArray(body.data)) {
                const duplicate = body.data.find((s) => (
                    s._id !== excludeSchemaId &&
                    normalizeValue(s.dataset_id).toLowerCase() === normalizedDatasetId &&
                    normalizeValue(s.purpose).toLowerCase() === normalizedPurpose
                ));

                return !!duplicate;
            }

            return false;
        } catch {
            return false;
        }
    };

    const validateSchemaForm = (data) => {
        const errors = {};

        if (!data || Object.keys(data).length === 0) {
            errors.json = "JSON data cannot be empty";
        }

        return errors;
    };
    const addSchema = () => navigate("/AddSchema");

    // EDIT / ADD ACTIONS
    const editSchema = async (id) => {
        if (!id) return;
        hud("Please wait...");
        try {
            const response = await ck_schemasDS.WS_CK_Schema_Object_By_ID_GET({ _id: id });
            const data = response.data;
            const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

            if (data.statusCode === 200 && body?.data) {
                let parsedData = typeof body.data === "string" ? JSON.parse(body.data) : body.data;
                navigate("/AddSchema", { state: { schemaToEdit: parsedData } });
            } else {
                insertLog(
                    `${userName} failed to fetch schema for edit`,
                    0,
                    useremail,
                    hud,
                    stopHudRotation,
                    showAlert,
                    id || '',
                    'GET',
                    'WS_CK_Schema_Object_By_ID_GET',
                    'FAILED',
                    'Unable to fetch schema for edit'
                );
                showAlert(body?.message || "Failed to fetch schema JSON for edit.", [
                    { label: "OK", color: "var(--buttonColor)", onClick: () => { } }
                ]);
            }
        } catch (err) {
            insertLog(
                `${userName} failed to fetch schema for edit`,
                0,
                useremail,
                hud,
                stopHudRotation,
                showAlert,
                id || '',
                'GET',
                'WS_CK_Schema_Object_By_ID_GET',
                'FAILED',
                err.message
            );
            showAlert(err.message || "Something went wrong while fetching schema for edit.", [
                { label: "OK", color: "var(--buttonColor)", onClick: () => { } }
            ]);
        } finally {
            stopHudRotation();
        }
    };
    return {
        // State
        displayedSchemas,
        openJSONModal,
        jsonSchema,
        isEditMode,
        schemaErrors,
        currentPage,
        pageSize,
        search,
        jsonText,
        startEntry,
        endEntry,
        totalRecords,
        totalPages,
        currentSchemaName,
        // Setters
        setOpenJSONModal,
        setCurrentPage,
        setPageSize,
        setSearch,
        // Functions
        addSchema,
        viewJSON,
        editSchema,
        handleEnableEdit,
        handleJsonChange,
        handleDownloadJSON,
        handleUpdateToServer,
        handleCancelEdit,
        handleCloseModal,
    };
};
